import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AuthShell from "@/components/AuthShell";
import PasswordInput from "@/components/PasswordInput";
import GoogleAuthButton from "@/components/GoogleAuthButton";

const inputCls = "w-full bg-surface-2 border border-border rounded-md px-4 py-3 text-fg text-sm focus:outline-none focus:ring-2 focus:ring-accent/60 focus:border-accent/40 transition-shadow";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("reason") === "session") {
      toast.warning("Vous avez été déconnecté car votre compte a été utilisé sur un autre appareil.", { duration: 6000 });
      window.history.replaceState({}, "", "/login");
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      login(data.token, data.user);
      toast.success("Connexion réussie");
      // Revenir à la destination initiale (deep-link email), sinon espace par défaut.
      const params = new URLSearchParams(window.location.search);
      const next = params.get("next");
      const safeNext = next && /^\/(app|admin)(\/|\?|$)/.test(decodeURIComponent(next))
        ? decodeURIComponent(next) : null;
      if (data.user?.role === "admin") navigate("/admin");
      else navigate(safeNext || "/app");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Connexion"
      subtitle="Accédez à votre espace interKO."
      footer={<>Pas encore de compte ? <Link to="/register" className="text-accent hover:underline" data-testid="link-register">Créer un compte</Link></>}
    >
      <GoogleAuthButton label="Se connecter avec Google" />
      <div className="flex items-center gap-3 my-5">
        <div className="flex-1 h-px bg-border" />
        <span className="text-xs text-muted">ou avec votre email</span>
        <div className="flex-1 h-px bg-border" />
      </div>
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div>
          <label className="overline block mb-2">Email</label>
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            className={inputCls} placeholder="vous@esn.fr" data-testid="login-email" />
        </div>
        <div>
          <div className="flex justify-between mb-2">
            <label className="overline">Mot de passe</label>
            <Link to="/forgot-password" className="text-xs text-accent hover:underline" data-testid="link-forgot">Oublié ?</Link>
          </div>
          <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} required testid="login-password" />
        </div>
        <button type="submit" disabled={loading} data-testid="login-submit"
          className="w-full bg-accent text-black font-semibold py-3 rounded-md hover-lift disabled:opacity-60">
          {loading ? "Connexion…" : "Se connecter"}
        </button>
      </form>
    </AuthShell>
  );
}
